import { useEffect, useMemo, useState } from "react";
import Header from "../../components/layout/Header/Header";
import Footer from "../../components/layout/Footer/Footer";
import Container from "../../components/layout/Container/Container";
import s from "./PricePage.module.scss";

import { useClinicInfo } from "../../api/queries/useClinicInfo";
import { toMap } from "../../app/clinicInfoMap";

import type { FC, SVGProps } from "react";
import { Link } from "react-router-dom";

import { ReactComponent as ToothIcon } from "../../assets/icons/price/tooth.svg";
import { ReactComponent as RootIcon } from "../../assets/icons/price/root.svg";
import { ReactComponent as SparkIcon } from "../../assets/icons/price/spark.svg";
import { ReactComponent as CrownIcon } from "../../assets/icons/price/crown.svg";
import { ReactComponent as ScalpelIcon } from "../../assets/icons/price/scalpel.svg";
import { ReactComponent as ImplantIcon } from "../../assets/icons/price/implant.svg";
import { ReactComponent as BracesIcon } from "../../assets/icons/price/braces.svg";
import { ReactComponent as KidIcon } from "../../assets/icons/price/kid.svg";

import { usePriceListByDirection } from "../../api/queries/usePriceListByDirection";
import type { PriceList } from "../../api/priceList";

type IconName =
  | "tooth"
  | "root"
  | "spark"
  | "crown"
  | "scalpel"
  | "implant"
  | "braces"
  | "kid";

type Direction = {
  id: string;
  title: string;
  icon?: IconName;
};

type NoteLines = string[];
type SvgIcon = FC<SVGProps<SVGSVGElement>>;

const ICONS: Record<IconName, SvgIcon> = {
  tooth: ToothIcon,
  root: RootIcon,
  spark: SparkIcon,
  crown: CrownIcon,
  scalpel: ScalpelIcon,
  implant: ImplantIcon,
  braces: BracesIcon,
  kid: KidIcon,
};

function safeJson<T>(value: string | undefined, fallback: T): T {
  try {
    return value ? (JSON.parse(value) as T) : fallback;
  } catch {
    return fallback;
  }
}

function toTel(phone: string) {
  return phone.replace(/[^\d+]/g, "");
}

// Телефон "под ключ": сначала contacts.phones (JSON массив), иначе contacts.phone
function getPrimaryPhone(cfg: Record<string, string | undefined>) {
  const raw = cfg["contacts.phones"];

  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const first = parsed.find((x) => typeof x === "string" && x.trim());
        if (first) return String(first).trim();
      }
    } catch {
      // ignore
    }
  }

  return (cfg["contacts.phone"] || "").trim();
}

// Нормализация направлений из clinic-info: защита от мусора/дубликатов/левых icon
function normalizeDirections(input: unknown): Direction[] {
  if (!Array.isArray(input)) return [];

  const allowedIcons = new Set<IconName>(Object.keys(ICONS) as IconName[]);
  const seen = new Set<string>();
  const result: Direction[] = [];

  for (const item of input) {
    if (!item || typeof item !== "object") continue;

    const anyItem = item as any;
    const id = typeof anyItem.id === "string" ? anyItem.id.trim() : "";
    const title = typeof anyItem.title === "string" ? anyItem.title : "";
    const iconRaw = typeof anyItem.icon === "string" ? anyItem.icon.trim() : "";
    const icon =
      iconRaw && allowedIcons.has(iconRaw as IconName) ? (iconRaw as IconName) : undefined;

    if (!id || !title.trim()) continue;
    if (seen.has(id)) continue;

    seen.add(id);
    result.push({ id, title, icon });
  }

  return result;
}

function Icon({ name }: { name?: IconName }) {
  const Svg = name ? ICONS[name] : undefined;
  const Fallback = ToothIcon;
  const Final = Svg ?? Fallback;
  return <Final className={s.icon} aria-hidden="true" />;
}

export default function PricePage() {
  const { data: info } = useClinicInfo();
  const cfg = toMap(info ?? []);

  const directionsTitle = "Услуги и стоимость\nпо направлениям";

  const hint =
    cfg["price.directionsHint"] ||
    cfg["price.note"] ||
    "Стоимость указана «под ключ» (с анестезией, расходными материалами, возможной коррекцией).";

  const directionsRaw = safeJson<unknown>(cfg["price.directions"], []);
  const directions = useMemo(() => normalizeDirections(directionsRaw), [directionsRaw]);

  const belowNoteLines = safeJson<NoteLines>(cfg["price.belowCards.noteLines"], [
    "Цены указаны для ознакомления и не являются публичной офертой.",
    "Окончательная стоимость лечения определяется после очного осмотра и диагностики.",
    "Полный прейскурант доступен в клинике.",
  ]);

  const consultTitle =
    cfg["price.consult.title"] || "Записаться\nна консультацию";

  const consultPhoneLabel =
    cfg["price.consult.phoneLabel"] ||
    "Оставьте заявку на консультацию, позвонив по номеру:";

  const phone =
    getPrimaryPhone(cfg as Record<string, string | undefined>) ||
    "+7 (000) 000-00-00";
  const tel = toTel(phone);

  // =============================
  // Модалка (sheet)
  // =============================
  const [openDirection, setOpenDirection] = useState<string | null>(null);

  const directionMap = useMemo(() => {
    const m = new Map<string, Direction>();
    directions.forEach((d) => m.set(d.id, d));
    return m;
  }, [directions]);

  useEffect(() => {
    if (openDirection && !directionMap.has(openDirection)) {
      setOpenDirection(null);
    }
  }, [openDirection, directionMap]);

  const currentDirection = openDirection ? directionMap.get(openDirection) : null;

  const { data: rows, isLoading, isError } = usePriceListByDirection(openDirection);

  const openSheet = (id: string) => setOpenDirection(id);
  const closeSheet = () => setOpenDirection(null);

  // ESC закрывает
  useEffect(() => {
    if (!openDirection) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeSheet();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDirection]);

  // блокируем скролл body, пока модалка открыта
  useEffect(() => {
    if (!openDirection) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [openDirection]);

  return (
    <div className={s.pageWrap}>
      <Header />

      <main className={s.page}>
        <div className={s.topGap} />

        <section className={s.cards}>
          <Container className={s.cardsContainer}>
            <div className={s.cardsHead}>
              <h2 className={s.h2}>{directionsTitle}</h2>

              <div className={s.hint}>
                
              </div>
            </div>

            {directions.length === 0 ? (
              <div style={{ marginTop: 18, opacity: 0.75, lineHeight: 1.55 }}>
                Направления не настроены. Зайдите в админку → «Прайс по направлениям» и заполните карточки
                (<code>price.directions</code>).
              </div>
            ) : (
              <div className={s.grid}>
                {directions.map((d) => (
                  <button
                    key={d.id}
                    className={s.card}
                    type="button"
                    onClick={() => openSheet(d.id)}
                  >
                    <div className={s.cardIcon}>
                      <Icon name={d.icon} />
                    </div>

                    <div className={s.cardTitle} lang="ru">
                      {d.title.split("\n").map((line, i) => (
                        <span key={i}>
                          {line}
                          {i < d.title.split("\n").length - 1 ? <br /> : null}
                        </span>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            )}

            <div className={s.belowCards}>
              <div className={s.belowNote}>
                {belowNoteLines.map((line, idx) => (
                  <div key={idx} className={s.belowLine}>
                    {line}
                  </div>
                ))}
              </div>

              <div className={s.consult}>
                <h3 className={s.consultTitle}>{consultTitle}</h3>

                <div className={s.consultPhoneRow}>
                  <span className={s.consultPhoneLabel}>{consultPhoneLabel}</span>{" "}
                  <a className={s.consultPhone} href={`tel:${tel}`}>
                    {phone}
                  </a>
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* =============================
            Белый лист (modal sheet)
           ============================= */}
        {openDirection ? (
          <div className={s.sheetBackdrop} onMouseDown={closeSheet} role="presentation">
            <div
              className={s.sheet}
              onMouseDown={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className={s.sheetHead}>
                <div className={s.sheetTitleBlock}>
                  <div className={s.sheetKicker}>Прайс</div>
                  <div className={s.sheetTitle}>
                    {currentDirection?.title?.replaceAll("\n", " ") || "Направление"}
                  </div>
                </div>

                <button
                  className={s.sheetClose}
                  type="button"
                  onClick={closeSheet}
                  aria-label="Закрыть"
                >
                  ✕
                </button>
              </div>

              <div className={s.sheetBody}>
                {isLoading ? (
                  <div className={s.sheetState}>Загрузка…</div>
                ) : isError ? (
                  <div className={s.sheetStateError}>
                    Не удалось загрузить прайс. Попробуйте позже.
                  </div>
                ) : (rows ?? []).length === 0 ? (
                  <div className={s.sheetState}>Пока нет цен для этого направления.</div>
                ) : (
                  <div className={s.sheetList}>
                    {(rows ?? []).map((r: PriceList) => (
                      <div key={(r as any).id} className={s.sheetRow}>
                        <div className={s.sheetRowMain}>
                          <div className={s.sheetRowTitle}>{(r as any).title || "—"}</div>
                          {(r as any).description ? (
                            <div className={s.sheetRowDesc}>{(r as any).description}</div>
                          ) : null}
                        </div>

                        <div className={s.sheetRowPrice}>{(r as any).price || "—"}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className={s.sheetFoot}>
                <div className={s.sheetFootNote}>
                  Уточняйте точную стоимость на консультации — она зависит от клинической ситуации.
                </div>
                <Link className={s.sheetFootLink} to="/contacts" onClick={closeSheet}>
                  Перейти в контакты
                </Link>
              </div>
            </div>
          </div>
        ) : null}
      </main>

      <Footer />
    </div>
  );
}
